import React, { useMemo } from 'react';
import {
  DataGrid,
  GridColDef,
  CellTypes,
  DataGridProps,
  Button,
} from '@fashionnovateam/nova-ui-toolkit';
import { find, get } from 'lodash';
import { Link } from 'react-router-dom';
import makeFactoryStatus from '../../Lib/Utils/Factory/make-factory-status';
import CountryList from '../../Lib/Constants/countries';
import { Factory } from '../../Lib/Api/factory';
import { vendorName } from '../../Lib/Utils/Helpers/vendor-helper';
import { User } from '../../Lib/Api/vendor';
import { hasAccessTo, RolesType } from '../../Lib/Constants/roles';

type FactoryRow = {
  id: number;
  vendor_name: CellTypes.DataGridCellCompanyParams;
  legal_name: CellTypes.DataGridCellStringParams;
  status: CellTypes.DataGridCellBadgeParams;
  contact_name: CellTypes.DataGridCellPersonParams;
  contact_email: CellTypes.DataGridCellEmailParams;
  contact_phone: CellTypes.DataGridCellStringParams;
  country: CellTypes.DataGridCellStringParams;
  audit_expiration_date: CellTypes.DataGridCellDateTimeParams;
  login_as: CellTypes.DataGridCellCustomParams;
};

const columns: GridColDef[] = [
  {
    field: 'vendor_name',
    headerName: 'Vendor Name',
    render: 'company',
    flex: 1,
    sortable: false,
    minWidth: 150,
    disableColumnMenu: true,
  },
  {
    field: 'status',
    headerName: 'Status',
    render: 'badge',
    flex: 1,
    sortable: false,
    minWidth: 150,
    disableColumnMenu: true,
  },
  {
    field: 'legal_name',
    headerName: 'Name',
    display: 'flex',
    flex: 1,
    sortable: true,
    minWidth: 150,
    disableColumnMenu: true,
  },
  {
    field: 'contact_name',
    headerName: 'Contact Name',
    render: 'person',
    sortable: true,
    flex: 2,
    minWidth: 150,
    disableColumnMenu: true,
  },
  {
    field: 'contact_email',
    headerName: 'Contact Email',
    render: 'email',
    sortable: false,
    flex: 1,
    minWidth: 150,
    disableColumnMenu: true,
  },
  {
    field: 'contact_phone',
    headerName: 'Contact Phone',
    display: 'flex',
    sortable: false,
    flex: 1,
    minWidth: 150,
    disableColumnMenu: true,
  },
  {
    field: 'country',
    headerName: 'Country',
    display: 'flex',
    sortable: false,
    flex: 1,
    minWidth: 130,
    disableColumnMenu: true,
  },
  {
    field: 'audit_expiration_date',
    headerName: 'Audit',
    render: 'dateTime',
    display: 'flex',
    sortable: true,
    flex: 1,
    minWidth: 100,
    disableColumnMenu: true,
  },
  {
    field: 'login_as',
    headerName: '',
    render: 'custom',
    display: 'flex',
    align: 'center',
    sortable: false,
    minWidth: 100,
    disableColumnMenu: true,
  },
];

type Props = {
  factories: Factory[];
  currentUser: User;
} & DataGridProps<FactoryRow>;

const statusMapping: {
  [key: string]: 'yellow' | 'green' | 'red' | undefined;
} = {
  Approved: 'green',
  Rejected: 'red',
  'Pending Review': 'yellow',
};

function FactoriesDataGrid({ factories, ...dataGridProps }: Props) {
  const transformedRows = useMemo(
    () =>
      factories.map((factory) => {
        const { vendor } = factory;
        const status = makeFactoryStatus(factory);
        return {
          id: factory.id,
          vendor_name: { name: vendorName(vendor) },
          status: {
            contrast: 'low',
            color: statusMapping[status],
            children: status,
          },
          contact_name: {
            name: `${factory.contact_name}`,
            badgeType: 'vendor',
            className: 'bg-neutral-200',
          },
          contact_email: factory.contact_email,
          contact_phone: factory.contact_phone,
          legal_name: factory.legal_name,
          country:
            get(
              find(CountryList, ({ value }) => value === factory.country),
              'text',
            ) || factory.country,
          audit_expiration_date: {
            date: factory.audit_expiration_date,
            time: false,
            activity: false,
          },
          login_as: hasAccessTo(
            'users/:id/factories',
            dataGridProps.currentUser.role as RolesType,
          ) ? (
            <Link
              to={`/users/${get(factory, 'vendor.contacts[0].id')}/factories/${factory.id}`}
            >
              <Button color="secondary">Login As</Button>
            </Link>
          ) : undefined,
        } satisfies FactoryRow;
      }),
    [factories],
  );

  return (
    <DataGrid
      stateName="factories_datagrid"
      hideFilterButton
      hideGroupButton
      rows={transformedRows}
      initialState={{
        pinnedColumns: {
          left: ['vendor_name'],
        },
      }}
      sortingMode="server"
      paginationMode="server"
      {...dataGridProps}
      columns={columns}
    />
  );
}

export default FactoriesDataGrid;
